// Trip money: profit, margin, fiscal-year labels and the CSV export. Safe to
// bundle in the browser, so the web app shares it with the API.

import { fiscalYearOf } from '../../../src/fiscal-year.ts';
import type { TripMoneyFacts, TripProfit, TripSummary } from './api-types.ts';

export const round2 = (n: number) => Math.round(n * 100) / 100;

/** profit / income as a fraction to four places; null on zero income. */
export const marginOf = (profit: number, income: number): number | null => (income === 0 ? null : Math.round((profit / income) * 10_000) / 10_000);

/** Profit and margin from income and cost: null without income; no margin on zero income. */
export function profitOf(income: number | null, cost: number): { profit: number | null; margin: number | null } {
  if (income === null) return { profit: null, margin: null };
  const profit = round2(income - cost);
  return { profit, margin: marginOf(profit, income) };
}

/**
 * Profit two ways. Projected: the invoice, else the trip price, less the contracted vendor cost or what's been
 * paid if that's more. Today: money received less Salesforce payments.
 */
export function tripProfit(f: TripMoneyFacts): TripProfit {
  const projectedIncome = f.invoiced ?? f.price;
  const projectedCost = round2(Math.max(f.contracted ?? 0, f.paid));
  const projected = profitOf(projectedIncome, projectedCost);
  const today = profitOf(f.received, f.paid);
  return {
    projectedIncome, projectedCost, projectedProfit: projected.profit, projectedMargin: projected.margin,
    profitToday: today.profit, marginToday: today.margin,
  };
}

/** Fiscal year by trip date, named for the year it ends: with a Dec 1 start, 'FY26' runs Dec 1, 2025 to Nov 30, 2026. */
export function fiscalYearLabel(date: string, fiscalYearStart: string): string {
  return `FY${String(fiscalYearOf(date, fiscalYearStart) % 100).padStart(2, '0')}`;
}
export const FY_PARAM = /^FY\d{2}$/;

const CSV_HEAD = [
  'Trip', 'Trip date', 'Trip price', 'Invoiced', 'Contracted cost', 'Projected cost', 'Projected profit', 'Projected margin %',
  'Received', 'Paid', 'Profit today', 'Margin today %', 'In Xero', 'Reconciled in Xero',
];
const percent = (m: number | null) => (m === null ? null : Math.round(m * 10_000) / 100);

function csvCell(v: string | number | null): string {
  if (v === null) return '';
  if (typeof v === 'number') return v.toFixed(2);
  // A leading = + - @ would run as a formula in a spreadsheet.
  const text = /^[=+\-@\t\r]/.test(v) ? `'${v}` : v;
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/** The trip list as CSV, one row per trip, in the order given. Money in dollars, margins in percent, empty for no value. */
export function tripsCsv(trips: TripSummary[]): string {
  const rows = trips.map((t) =>
    [
      t.name, t.date, t.price, t.invoiced, t.contracted, t.projectedCost, t.projectedProfit, percent(t.projectedMargin),
      t.received, t.paid, t.profitToday, percent(t.marginToday), t.inXero, t.reconciledAmount,
    ].map(csvCell).join(','),
  );
  return [CSV_HEAD.join(','), ...rows].join('\r\n') + '\r\n';
}
